import {
  HttpPostRequestModel,
  HttpPutRequestModel,
  HttpRequestModelType,
  __HttpRequestModel__,
} from 'shared/domain/httpRequest/httpRequestModel';
import type {
  IssueWorkflowOutDto,
  WorkflowCreateInDto,
  WorkflowUpdateInDto,
} from 'shared/domain/workflow/types';
import {
  IWorkflowUploader,
  UploadWorkflow,
  UploadWorkflowChanges,
} from 'serviceWorker/repository/workflow/uploadWorkflow';
import type { IHttpRequestRunner } from 'shared/domain/httpRequest/httpRequestRunner';

export type WorkflowCreateRequestModel = HttpPostRequestModel<
  {
    workflowCreateDto: WorkflowCreateInDto;
    projectId: string;
    uniqueId: string;
  },
  HttpRequestModelType.workflow
>;

export type WorkflowUpdateRequestModel = HttpPutRequestModel<
  {
    workflowUpdateDto: WorkflowUpdateInDto;
    workflowId: string;
    projectId: string;
    uniqueId: string;
  },
  HttpRequestModelType.workflow
>;

export class WorkflowRequestRunner implements IHttpRequestRunner {
  constructor(
    private workflowUploader: IWorkflowUploader,
    private uploadWorkflowUseCase: UploadWorkflow,
    private uploadWorkflowChangesUseCase: UploadWorkflowChanges,
    /** Stores the workflow the server answered, so reads after the change are not stale. */
    private storeWorkflow: (workflow: IssueWorkflowOutDto) => Promise<unknown>,
    /** Repairs the stored workflows when storing the saved one fails. */
    private syncWorkflows: () => void
  ) {}

  async execute(
    request:
      | __HttpRequestModel__
      | WorkflowCreateRequestModel
      | WorkflowUpdateRequestModel
  ): Promise<IssueWorkflowOutDto> {
    const workflow = await this.upload(request);
    if (workflow?._id) {
      await this.storeWorkflowOrSync(workflow);
    }
    return workflow;
  }

  /** The server already saved the workflow: a local storage failure must not report the change as failed. */
  private async storeWorkflowOrSync(
    workflow: IssueWorkflowOutDto
  ): Promise<void> {
    try {
      await this.storeWorkflow(workflow);
    } catch {
      this.syncWorkflows();
    }
  }

  private async upload(
    request:
      | __HttpRequestModel__
      | WorkflowCreateRequestModel
      | WorkflowUpdateRequestModel
  ): Promise<IssueWorkflowOutDto> {
    switch (request.method) {
      case 'POST': {
        return this.uploadWorkflow(request as WorkflowCreateRequestModel);
      }
      case 'PUT': {
        return this.uploadWorkflowChanges(
          request as WorkflowUpdateRequestModel
        );
      }
      default: {
        throw new Error(`Unsupported method for workflow: ${request.method}`);
      }
    }
  }

  private async uploadWorkflow(
    request: WorkflowCreateRequestModel
  ): Promise<IssueWorkflowOutDto> {
    return this.uploadWorkflowUseCase(
      this.workflowUploader,
      request.data.workflowCreateDto,
      request.url
    );
  }

  private async uploadWorkflowChanges(
    request: WorkflowUpdateRequestModel
  ): Promise<IssueWorkflowOutDto> {
    return this.uploadWorkflowChangesUseCase(
      this.workflowUploader,
      request.data.workflowUpdateDto,
      request.url
    );
  }
}
